import {
    Avatar,
    Box,
    Card,
    Chip,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import useWindowSize from "../../../../hooks/useWindowSize";

const headCellSx = {
    fontWeight: 700,
    fontSize: 13,
    color: "#fff",
    backgroundColor: "var(--color-primary-background)",
    whiteSpace: "nowrap",
};

function getInitials(name = "") {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
}

function InfoRow({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600} noWrap>{value}</Typography>
        </Stack>
    );
}

export default function RequestsList({ requests }) {
    const width = useWindowSize();
    const isDesktop = width >= 1280;

    if (isDesktop) {
        return (
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 3, border: "1px solid #e3e6ea", maxHeight: "68vh" }}
            >
                <Table stickyHeader size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx} align="center">Empresa</TableCell>
                            <TableCell sx={headCellSx} align="center">CNPJ</TableCell>
                            <TableCell sx={headCellSx} align="center">Email</TableCell>
                            <TableCell sx={headCellSx} align="center">Merchant ID</TableCell>
                            <TableCell sx={headCellSx} align="center">Status</TableCell>
                            <TableCell sx={headCellSx} align="center">Data da solicitação</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {requests.map((item, index) => (
                            <TableRow
                                key={item.id ?? index}
                                hover
                                sx={{
                                    backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
                                    "&:last-child td": { borderBottom: 0 },
                                }}
                            >
                                <TableCell>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                fontSize: 13,
                                                bgcolor: "var(--color-primary-background)",
                                            }}
                                        >
                                            {getInitials(item.company_name)}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight={600}>{item.company_name}</Typography>
                                    </Stack>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{item.company_document}</Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">{item.company_email}</Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, textAlign: 'center' }}>{item.merchant_id}</Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <Chip
                                        label={item.status ? "Integrado" : "Pendente"}
                                        color={item.status ? "success" : "warning"}
                                        size="small"
                                    />
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {new Date(item.created_at).toLocaleDateString("pt-BR")}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <Stack spacing={2}>
            {requests.map((item, index) => (
                <Card key={item.id ?? index} variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: "var(--color-primary-background)" }}>
                            {getInitials(item.company_name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>{item.company_name}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>{item.company_email}</Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                        <Chip
                            label={item.status ? "Integrado" : "Pendente"}
                            color={item.status ? "success" : "warning"}
                            size="small"
                        />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1}>
                        <InfoRow label="CNPJ" value={item.company_document} />
                        <InfoRow label="Merchant ID" value={item.merchant_id} />
                        <InfoRow label="Solicitado em" value={new Date(item.created_at).toLocaleDateString("pt-BR")} />
                    </Stack>
                </Card>
            ))}
        </Stack>
    );
}
